import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, Scroll } from '@angular/router';
import { AbstractAlertService } from '@zero/shared/util-global-state';
import { UserMessage } from '@zero/shared/util-models';
import { BehaviorSubject, Observable, Subject, Subscription, interval, map, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserMessagesHolderService extends AbstractAlertService implements OnDestroy {
  private readonly _pushMessages = new BehaviorSubject<UserMessage[]>([]);
  public readonly pushMessages$ = this._pushMessages.asObservable();

  public readonly message$: Observable<UserMessage> = this.pushMessages$.pipe(map((messages) => messages[0]));
  public readonly hasMessages$: Observable<boolean> = this.pushMessages$.pipe(map((messages) => messages.length > 0));

  private readonly destroy$ = new Subject<void>();

  constructor(private router: Router) {
    super();
    this.subscribeToInterval();
    this.subscribeToRouteEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private subscribeToInterval(): Subscription {
    return interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const checkTime = new Date().getTime();
        const regex = new RegExp(/\(\d+s\)/);

        const newMessages = this._pushMessages.value.filter((msg: UserMessage) => {
          const toBeDeleted = msg.deleteTimeStamp && msg.deleteTimeStamp <= checkTime;

          if (!toBeDeleted && msg.deleteTimeStamp) {
            const timeToShow = ` (${Math.ceil(Math.abs((checkTime - msg.deleteTimeStamp) / 1000))}s)`;
            if (RegExp(regex).exec(msg.message)) {
              msg.message = msg.message.replace(regex, timeToShow);
            } else {
              msg.message += timeToShow;
            }
          }

          return !toBeDeleted;
        });
        this._pushMessages.next(newMessages);
      });
  }

  private subscribeToRouteEvents(): Subscription {
    return this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      const newMessages = this._pushMessages.value.filter((msg: UserMessage) => {
        let deleteWithNextRouteEvent = false;
        const routeEvent = (event as Scroll).routerEvent;
        if (routeEvent instanceof NavigationStart || routeEvent instanceof NavigationEnd) {
          deleteWithNextRouteEvent = typeof msg.deleteWithNextRouteEvent !== 'undefined' && msg.deleteWithNextRouteEvent === true;
        }
        return !deleteWithNextRouteEvent;
      });
      this._pushMessages.next(newMessages);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async showInfoAlert(message: unknown, _duration?: number): Promise<void> {
    return this.addInfoMessage(message as string);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async showSuccessAlert(message: unknown, _duration?: number): Promise<void> {
    return this.addSuccessMessage(message as string);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async showErrorAlert(message: unknown, _duration?: number): Promise<void> {
    return this.addErrorMessage(message as string);
  }

  public async clearAlerts(): Promise<void> {
    return this.clearAlerts();
  }

  public addMessage(msg: UserMessage, addTime?: boolean) {
    if (addTime) {
      msg.message = `${new Date().toLocaleTimeString()}: ${msg.message}`;
    }

    if (this._pushMessages.value.findIndex((m) => m.message === msg.message) === -1) {
      this._pushMessages.next([msg, ...this._pushMessages.value]);
    }
  }

  public addErrorMessage(msg: string, addTime?: boolean) {
    this.addMessage(new UserMessage(msg, 'danger', true), addTime);
  }

  public addInfoMessage(msg: string, addTime?: boolean) {
    this.addMessage(new UserMessage(msg, 'info', true), addTime);
  }

  public addSuccessMessage(msg: string, addTime?: boolean) {
    this.addMessage(new UserMessage(msg, 'success', true), addTime);
  }

  public clearAllMessages() {
    this._pushMessages.next([]);
  }

  public removeMessage(msg: UserMessage) {
    const index = this._pushMessages.value.indexOf(msg);
    if (index > -1) {
      const messages = this._pushMessages.value;
      messages.splice(index, 1);
      this._pushMessages.next(messages);
    }
  }
}
